import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import IdentityVerificationButton from "@/components/ui/identity_verification_button";
import { Body, Link, SectionHeader } from "@sprout/typography";
import IdentityVerificationModalHeader from "@/components/ui/identity_verification_modal_header";
import IdCardIcon from "@sprout/icons/id_card";
import SmartphoneIcon from "@sprout/icons/smartphone";
import useTranslation from "@hooks/use_translation";
import { TertiaryButton } from "@sprout/buttons";
import {
  identityVerificationPartnerConfig,
  isIdentityVerificationPartner,
} from "@/utils/identity_verification_provider";

interface IdentityVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: () => void;
  companyName: string;
  partner?: string | null;
}

const IdentityVerificationModal: React.FC<IdentityVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerify,
  companyName,
  partner,
}) => {
  const { t } = useTranslation("identity_verification");
  // partner can go nullish while the modal is open (a refetch after the
  // recruiter cancels the task); render nothing rather than throw.
  const partnerConfig =
    isOpen && isIdentityVerificationPartner(partner)
      ? identityVerificationPartnerConfig(partner)
      : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {partnerConfig && (
        <DialogContent
          onOpenAutoFocus={(e: Event) => e.preventDefault()}
          className="sm:max-w-xl p-6 rounded-lg max-h-[90vh] overflow-y-auto"
        >
          <IdentityVerificationModalHeader partner={partner} />

          <div className="mb-6">
            <SectionHeader className="!mb-4">
              {t("modal.title", { provider_name: partnerConfig.displayName })}
            </SectionHeader>

            <Body className="text-base !mb-8">
              {t("modal.intro_html", {
                company_name: companyName,
                provider_name: partnerConfig.displayName,
              })}
            </Body>

            <div className="space-y-4 mb-4">
              <div className="flex items-center px-3">
                <span className="mr-3">
                  <SmartphoneIcon size="md" />
                </span>
                <Body className="text-base">{t("modal.requirements.phone")}</Body>
              </div>

              <div className="flex items-center px-3">
                <span className="mr-3">
                  <IdCardIcon size="md" />
                </span>
                <Body className="text-base">{t("modal.requirements.id")}</Body>
              </div>
            </div>

            <Body className="text-base">
              {t(`modal.privacy_html.${partnerConfig.translationKey}`, {
                provider_name: partnerConfig.displayName,
              })}
              {partnerConfig.learnMoreHref && (
                <>
                  {" "}
                  <Link href={partnerConfig.learnMoreHref} external>
                    {t("modal.learn_more")}
                  </Link>
                </>
              )}
            </Body>
          </div>

          <div className="flex flex-col items-center gap-3 mx-auto">
            <IdentityVerificationButton partner={partner} onClick={onVerify} />
            <TertiaryButton onClick={onClose}>{t("modal.not_now")}</TertiaryButton>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default IdentityVerificationModal;
